import { makeAutoObservable } from 'mobx';
import createStoreContext from '../createStoreContext';
import type { MarketplaceReviewPromptState } from 'src/types/marketplaceReview';
import {
  MARKETPLACE_REVIEW_PROMPT_COOLDOWN_MS,
  MARKETPLACE_REVIEW_PROMPT_DEFAULT_STATE,
  MARKETPLACE_REVIEW_PROMPT_STORAGE_KEY,
  MARKETPLACE_REVIEW_SHOW_PROBABILITY,
} from 'src/constants/marketplaceReview';

export const [MarketplaceReviewStoreProvider, useMarketplaceReviewStore] =
  createStoreContext(
    class MarketplaceReviewStore {
      isPromptOpen = false;
      private promptState: MarketplaceReviewPromptState =
        MARKETPLACE_REVIEW_PROMPT_DEFAULT_STATE;
      private isInitialized = false;

      constructor() {
        makeAutoObservable(this);
        this.initializeFromStorage();
      }

      private createDefaultState(): MarketplaceReviewPromptState {
        return {
          ...MARKETPLACE_REVIEW_PROMPT_DEFAULT_STATE,
        };
      }

      private initializeFromStorage(): void {
        if (this.isInitialized) {
          return;
        }

        if (typeof window === 'undefined') {
          this.promptState = this.createDefaultState();
          this.isInitialized = true;

          return;
        }

        try {
          const storedValue = window.localStorage.getItem(
            MARKETPLACE_REVIEW_PROMPT_STORAGE_KEY,
          );

          if (storedValue) {
            const parsedState = JSON.parse(storedValue);

            this.promptState = this.normalizeState(parsedState);
          } else {
            this.promptState = this.createDefaultState();
          }
        } catch {
          this.promptState = this.createDefaultState();
        }

        this.isInitialized = true;
      }

      private normalizeState(
        rawState?: MarketplaceReviewPromptState | null,
      ): MarketplaceReviewPromptState {
        const lastPromptedAt = Number(rawState?.lastPromptedAt);
        const isValidLastPromptedAt = Number.isFinite(lastPromptedAt);

        return {
          lastPromptedAt: isValidLastPromptedAt ? lastPromptedAt : null,
          dismissedPermanently: Boolean(rawState?.dismissedPermanently),
        };
      }

      private persistState(): void {
        if (typeof window === 'undefined') {
          return;
        }

        try {
          window.localStorage.setItem(
            MARKETPLACE_REVIEW_PROMPT_STORAGE_KEY,
            JSON.stringify(this.promptState),
          );
        } catch {
          // no-op
        }
      }

      private isCooldownComplete(): boolean {
        const lastPromptedAt = this.promptState.lastPromptedAt;

        if (lastPromptedAt === null) {
          return true;
        }

        return (
          Date.now() - lastPromptedAt >= MARKETPLACE_REVIEW_PROMPT_COOLDOWN_MS
        );
      }

      private markPromptSeen(): void {
        this.promptState = {
          ...this.promptState,
          lastPromptedAt: Date.now(),
        };
        this.persistState();
      }

      maybeOpenPrompt = (): void => {
        this.initializeFromStorage();

        if (this.isPromptOpen || this.promptState.dismissedPermanently) {
          return;
        }

        if (!this.isCooldownComplete()) {
          return;
        }

        const shouldOpenPrompt =
          Math.random() < MARKETPLACE_REVIEW_SHOW_PROBABILITY;

        if (!shouldOpenPrompt) {
          return;
        }

        this.markPromptSeen();
        this.isPromptOpen = true;
      };

      dismissPrompt = (): void => {
        if (!this.isPromptOpen) {
          return;
        }

        this.isPromptOpen = false;
        this.markPromptSeen();
      };

      dismissPromptPermanently = (): void => {
        this.isPromptOpen = false;
        this.promptState = {
          ...this.promptState,
          dismissedPermanently: true,
          lastPromptedAt: Date.now(),
        };
        this.persistState();
      };
    },
  );

export type MarketplaceReviewStore = ReturnType<
  typeof useMarketplaceReviewStore
>;
